import { useState, Fragment } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";
import {
    ArrowLeft, Plus, Upload, CheckCircle2, Search,
    Loader2, Trash2, Pencil, Users, ChevronLeft, ChevronRight,
    ListFilter, Building2, AlertTriangle, Receipt, XCircle, Mail, Phone,
    ChevronDown, User, Download, RefreshCw // ✅ Import Download ditambahkan
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import toast from "react-hot-toast";
import axios from "axios";

// --- TYPES ---
interface MasterData {
    id: number;
    title: string;
    institution_name: string;
    pic_name: string;
    total_amount: string;
    invoice_number: string | null;
    payment_status: string;
    status: string;
}

interface MemberData {
    id: number;
    collective_billing_id: number;
    conference_id: number;
    package_id: number | null;
    ojs_submission_id: number | null;
    submission_title: string | null;
    participant_name: string;
    participant_type: string;
    institution_name: string | null;
    presentation_format: string;
    is_presenter: boolean;
    is_internal_student: boolean;
    quantity: number;
    email: string;
    whatsapp_number: string;
    individual_amount: string;
    is_valid: boolean;
    validation_message: string;
    error_code: string | null; // ✅ Tambahan error_code
    verification_status: string; // ✅ Tambahan verification_status
}

interface OjsLookupData {
    ojs_submission_id: number;
    title: string;
    authors: string | null;
    authors_emails: string | null;
    authors_affiliations: string | null;
}

interface ConferenceOption {
    conference_id: number;
    name: string;
    slug: string;
}

interface MemberFormData {
    conference_id: string;
    participant_name: string;
    email: string;
    whatsapp_number: string;
    institution_name: string;
    participant_type: string;
    is_internal_student: boolean;
    is_presenter: boolean;
    ojs_submission_id: string;
    quantity: number;
    presentation_format: string;
}

const initialForm: MemberFormData = {
    conference_id: "", participant_name: "", email: "", whatsapp_number: "", institution_name: "",
    participant_type: "general", is_internal_student: false, is_presenter: false,
    ojs_submission_id: "", quantity: 1, presentation_format: "oral"
};

const formatRupiah = (val: string | number) => {
    return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(Number(val));
};

export default function CollectiveDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    // States
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState("10");
    const [search, setSearch] = useState("");

    // ✅ Tambahan State untuk Filter Baru
    const [isValidFilter, setIsValidFilter] = useState("all");
    const [errorCodeFilter, setErrorCodeFilter] = useState("all");
    const [isPresenterFilter, setIsPresenterFilter] = useState("all");
    const [participantTypeFilter, setParticipantTypeFilter] = useState("all");
    const [verificationStatusFilter, setVerificationStatusFilter] = useState("all");

    // Search state untuk Dropdown OJS
    const [ojsSearch, setOjsSearch] = useState("");

    // Modal States
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
    const [isCancelInvoiceModalOpen, setIsCancelInvoiceModalOpen] = useState(false);

    const [selectedMember, setSelectedMember] = useState<MemberData | null>(null);
    const [formData, setFormData] = useState<MemberFormData>(initialForm);
    const [importFile, setImportFile] = useState<File | null>(null);
    const [gatewayId, setGatewayId] = useState<string>("8"); // Default BNI IDR

    // --- FETCH MASTER DATA ---
    const { data: masterData, isLoading: masterLoading } = useQuery({
        queryKey: ["collective-master", id],
        queryFn: async (): Promise<MasterData> => {
            const { data } = await axiosClient.get(`/private/collective-billing/${id}`);
            return data.data;
        },
        enabled: !!id,
    });

    // --- FETCH MEMBERS DATA ---
    const { data: membersData, isLoading: membersLoading, isFetching } = useQuery({
        // ✅ Menyertakan state filter baru ke dalam queryKey
        queryKey: ["collective-members", id, page, perPage, search, isValidFilter, errorCodeFilter, isPresenterFilter, participantTypeFilter, verificationStatusFilter],
        queryFn: async () => {
            let url = `/private/collective-billing/${id}/members?page=${page}&limit=${perPage}`;
            if (search) url += `&search=${search}`;

            // ✅ Append Filter Baru ke URL API
            if (isValidFilter !== "all") url += `&is_valid=${isValidFilter}`;
            if (errorCodeFilter !== "all") url += `&error_code=${errorCodeFilter}`;
            if (isPresenterFilter !== "all") url += `&is_presenter=${isPresenterFilter}`;
            if (participantTypeFilter !== "all") url += `&participant_type=${participantTypeFilter}`;
            if (verificationStatusFilter !== "all") url += `&verification_status=${verificationStatusFilter}`;

            const { data } = await axiosClient.get(url);
            return data.data;
        },
        placeholderData: keepPreviousData,
        enabled: !!id,
    });

    // --- FETCH CONFERENCES ---
    const { data: conferencesData } = useQuery({
        queryKey: ["conferences-dropdown"],
        queryFn: async (): Promise<ConferenceOption[]> => {
            const { data } = await axiosClient.get(`/conference-billing/conferences`);
            return data.data;
        }
    });

    // --- FETCH OJS LOOKUP ---
    const { data: ojsList, isLoading: ojsLoading } = useQuery({
        queryKey: ["ojs-lookup", formData.conference_id],
        queryFn: async (): Promise<OjsLookupData[]> => {
            if (!formData.conference_id) return [];
            const { data } = await axiosClient.get(`/private/collective-billing/look-up-ojs/${formData.conference_id}`);
            return data.data;
        },
        enabled: !!formData.conference_id && formData.is_presenter,
    });

    // --- FILTER OJS LIST ---
    const filteredOjsList = ojsList?.filter(ojs =>
        ojs.title?.toLowerCase().includes(ojsSearch.toLowerCase()) ||
        ojs.ojs_submission_id.toString().includes(ojsSearch) ||
        ojs.authors?.toLowerCase().includes(ojsSearch.toLowerCase())
    );

    // --- MUTATIONS ---
    const handleError = (error: unknown) => {
        if (axios.isAxiosError(error) && error.response?.data?.message) {
            toast.error(error.response.data.message);
        } else {
            toast.error("Terjadi kesalahan sistem.");
        }
    };

    const saveMutation = useMutation({
        mutationFn: async (payload: MemberFormData) => {
            const submitData = {
                ...payload,
                ojs_submission_id: payload.is_presenter ? payload.ojs_submission_id : null
            };

            if (selectedMember) {
                return await axiosClient.put(`/private/collective-billing/${id}/members/${selectedMember.id}`, submitData);
            }
            return await axiosClient.post(`/private/collective-billing/${id}/members`, submitData);
        },
        onSuccess: (res) => {
            toast.success(res.data.message || "Anggota berhasil disimpan!");
            queryClient.invalidateQueries({ queryKey: ["collective-members"] });
            queryClient.invalidateQueries({ queryKey: ["collective-master"] });
            closeFormModal();
        },
        onError: handleError
    });

    const deleteMutation = useMutation({
        mutationFn: async (memberId: number) => {
            return await axiosClient.delete(`/private/collective-billing/${id}/members/${memberId}`);
        },
        onSuccess: (res) => {
            toast.success(res.data.message || "Anggota berhasil dihapus!");
            queryClient.invalidateQueries({ queryKey: ["collective-members"] });
            queryClient.invalidateQueries({ queryKey: ["collective-master"] });
            setIsDeleteModalOpen(false);
            setSelectedMember(null);
        },
        onError: handleError
    });

    const importMutation = useMutation({
        mutationFn: async (file: File) => {
            const fd = new FormData();
            fd.append("file_excel", file);
            return await axiosClient.post(`/private/collective-billing/${id}/members/import`, fd, {
                headers: { "Content-Type": "multipart/form-data" }
            });
        },
        onSuccess: (res) => {
            toast.success(res.data.message || "Import berhasil!");
            queryClient.invalidateQueries({ queryKey: ["collective-members"] });
            queryClient.invalidateQueries({ queryKey: ["collective-master"] });
            setIsImportModalOpen(false);
            setImportFile(null);
        },
        onError: handleError
    });

    const approveMutation = useMutation({
        mutationFn: async () => {
            return await axiosClient.post(`/private/collective-billing/${id}/members/approve`, {
                gateway_transaction_id: gatewayId
            });
        },
        onSuccess: (res) => {
            toast.success(res.data.message || "Tagihan berhasil digenerate!");
            queryClient.invalidateQueries({ queryKey: ["collective-master"] });
            setIsApproveModalOpen(false);
        },
        onError: handleError
    });

    const syncMutation = useMutation({
        mutationFn: async () => {
            return await axiosClient.post(`/private/collective-billing/${id}/members/sync-status`);
        },
        onSuccess: (res) => {
            toast.success(res.data.message || "Sync selesai.");
            queryClient.invalidateQueries({ queryKey: ["collective-members"] });
            queryClient.invalidateQueries({ queryKey: ["collective-master"] });
            setIsSyncModalOpen(false);
        },
        onError: handleError
    });

    const cancelInvoiceMutation = useMutation({
        mutationFn: async (memberId: number) => {
            return await axiosClient.post(`/private/collective-billing/${id}/members/${memberId}/cancel-individual-invoice`);
        },
        onSuccess: (res) => {
            toast.success(res.data.message || "Tagihan berhasil dibatalkan!");
            queryClient.invalidateQueries({ queryKey: ["collective-members"] });
            queryClient.invalidateQueries({ queryKey: ["collective-master"] });
            setIsCancelInvoiceModalOpen(false);
            setSelectedMember(null);
        },
        onError: handleError
    });

    // --- HANDLERS ---
    const openAddModal = () => {
        setSelectedMember(null);
        setFormData({ ...initialForm, institution_name: masterData?.institution_name || "" });
        setOjsSearch("");
        setIsFormModalOpen(true);
    };

    const openEditModal = (item: MemberData) => {
        setSelectedMember(item);
        setFormData({
            conference_id: String(item.conference_id || ""),
            participant_name: item.participant_name,
            email: item.email,
            whatsapp_number: item.whatsapp_number,
            institution_name: item.institution_name || "",
            participant_type: item.participant_type || "general",
            is_internal_student: item.is_internal_student,
            is_presenter: item.is_presenter,
            ojs_submission_id: String(item.ojs_submission_id || ""),
            quantity: item.quantity,
            presentation_format: item.presentation_format || "oral"
        });
        setOjsSearch("");
        setIsFormModalOpen(true);
    };

    const closeFormModal = () => {
        setIsFormModalOpen(false);
        setSelectedMember(null);
        setFormData(initialForm);
        setOjsSearch("");
    };

    // AUTO-FILL LOGIC
    const handleOjsSelect = (val: string) => {
        const selectedOjs = ojsList?.find(o => String(o.ojs_submission_id) === val);

        let autoName = formData.participant_name;
        let autoEmail = formData.email;
        let autoInst = formData.institution_name;

        if (selectedOjs) {
            if (selectedOjs.authors) {
                try {
                    const parsed = JSON.parse(selectedOjs.authors);
                    if (Array.isArray(parsed) && parsed.length > 0) autoName = parsed[0];
                } catch {
                    autoName = selectedOjs.authors.split(',')[0].trim();
                }
            }
            if (selectedOjs.authors_emails) {
                try {
                    const parsed = JSON.parse(selectedOjs.authors_emails);
                    if (Array.isArray(parsed) && parsed.length > 0) autoEmail = parsed[0];
                } catch {
                    autoEmail = selectedOjs.authors_emails.split(',')[0].trim();
                }
            }
            if (selectedOjs.authors_affiliations) {
                try {
                    const parsed = JSON.parse(selectedOjs.authors_affiliations);
                    if (Array.isArray(parsed) && parsed.length > 0) autoInst = parsed[0];
                } catch {
                    autoInst = selectedOjs.authors_affiliations.split(',')[0].trim();
                }
            }
        }

        setFormData({
            ...formData,
            ojs_submission_id: val,
            participant_name: autoName,
            email: autoEmail,
            institution_name: autoInst
        });
    };

    // ✅ HELPER: Render Status Verifikasi (Lifecycle)
    const renderVerificationStatus = (status: string) => {
        switch (status) {
            case 'not_requested':
            case 'not_required':
                return <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border border-slate-200">Draft</span>;
            case 'pending':
                return <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border border-amber-200">Menunggu</span>;
            case 'approved':
                return <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border border-emerald-200">Disetujui</span>;
            case 'rejected':
                return <span className="bg-rose-100 text-rose-700 px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border border-rose-200">Ditolak</span>;
            default:
                return <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border border-slate-200">{status}</span>;
        }
    };

    // ✅ HELPER: Render Badge Is_Valid beserta Error Code & Tooltip Dictionary
    const renderValidationBadge = (isValid: boolean, errorCode: string | null, message: string) => {
        if (isValid) {
            return (
                <div className="flex items-center gap-1.5 text-blue-600 bg-blue-50 dark:bg-blue-900/30 px-2.5 py-1 rounded-md w-fit border border-blue-200/50">
                    <CheckCircle2 className="w-3.5 h-3.5" /> <span className="text-[10px] font-bold uppercase tracking-wider">Siap Proses</span>
                </div>
            );
        }

        // Definisi kamus Error Code
        let config = {
            color: "text-rose-600 bg-rose-50 dark:bg-rose-900/30 border border-rose-200/50",
            icon: <XCircle className="w-3.5 h-3.5" />,
            label: "Error"
        };

        if (errorCode === 'PACKAGE_NOT_FOUND' || errorCode === 'OJS_NOT_FOUND' || errorCode === 'IN_OTHER_COLLECTIVE_DRAFT') {
            config = { color: "text-rose-600 bg-rose-50 dark:bg-rose-900/30 border border-rose-200/50", icon: <XCircle className="w-3.5 h-3.5" />, label: "Gagal (Danger)" };
        } else if (errorCode === 'OJS_NOT_READY_TO_PAY') {
            config = { color: "text-orange-600 bg-orange-50 dark:bg-orange-900/30 border border-orange-200/50", icon: <AlertTriangle className="w-3.5 h-3.5" />, label: "Tertunda" };
        } else if (errorCode === 'ALREADY_PAID_REGULAR') {
            config = { color: "text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200/50", icon: <CheckCircle2 className="w-3.5 h-3.5" />, label: "Aman (Lunas)" };
        } else if (errorCode === 'HAS_UNPAID_REGULAR') {
            config = { color: "text-amber-600 bg-amber-50 dark:bg-amber-900/30 border border-amber-200/50", icon: <AlertTriangle className="w-3.5 h-3.5" />, label: "Perhatian" };
        }

        return (
            <div className={`group/err relative flex items-center gap-1.5 px-2.5 py-1 rounded-md w-fit cursor-help ${config.color}`}>
                {config.icon} <span className="text-[10px] font-bold uppercase tracking-wider">{config.label}</span>
                {/* Tooltip Error Dictionary */}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover/err:block w-64 p-3 bg-slate-900 text-white text-[10px] rounded-xl shadow-xl z-50 whitespace-normal text-center leading-relaxed font-medium">
                    {message}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-slate-900" />
                </div>
            </div>
        );
    };

    // Helper: selected OJS title to show in SelectValue (fixes missing selected label when modal opens)
    const selectedOjsOption = ojsList?.find(o => String(o.ojs_submission_id) === formData.ojs_submission_id);
    const selectedOjsLabel = selectedOjsOption?.title ?? selectedMember?.submission_title ?? "";

    if (masterLoading) return <div className="p-10 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
    if (!masterData) return <div className="p-10 text-center">Data tidak ditemukan.</div>;

    const isMasterApproved = masterData.status === "approved";

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0 animate-in fade-in duration-500">
            {/* --- HEADER BACK & MASTER INFO --- */}
            <div>
                <Button variant="ghost" onClick={() => navigate("/collective-billing")} className="mb-4 text-slate-500 hover:text-slate-800 dark:hover:text-white -ml-4">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Kembali ke Master Kolektif
                </Button>

                <div className="bg-white dark:bg-slate-900 p-6 md:p-8 rounded-[2rem] shadow-sm border border-slate-100 dark:border-slate-800 flex flex-col lg:flex-row justify-between gap-6">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2.5 rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">
                                <Building2 className="w-6 h-6" />
                            </div>
                            <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">{masterData.title}</h1>
                        </div>
                        <div className="flex flex-wrap gap-4 mt-4 text-sm font-medium text-slate-600 dark:text-slate-300">
                            <span className="flex items-center gap-1.5"><User className="w-4 h-4 text-slate-400" /> {masterData.pic_name}</span>
                            <span className="flex items-center gap-1.5"><Receipt className="w-4 h-4 text-slate-400" /> {masterData.invoice_number || "Draft (Belum di-approve)"}</span>
                        </div>
                    </div>

                    <div className="flex flex-col items-start lg:items-end justify-center bg-slate-50 dark:bg-slate-800/50 p-5 rounded-2xl border border-slate-100 dark:border-slate-800">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Total Tagihan Akumulasi</p>
                        <p className="text-3xl font-black text-blue-600 dark:text-blue-400">{formatRupiah(masterData.total_amount)}</p>
                    </div>
                </div>
            </div>

            {/* --- TOOLBAR MEMBERS --- */}
            <div className="flex flex-col gap-4 bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm">

                <div className="flex flex-col xl:flex-row gap-4 items-center justify-between w-full">
                    {/* Search Bar */}
                    <div className="relative w-full xl:w-1/3 group">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input placeholder="Cari nama, naskah..." className="pl-10 w-full bg-slate-50 dark:bg-slate-950 rounded-xl" style={{ height: '45px' }} value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
                    </div>

                    <div className="flex flex-wrap items-center gap-3 w-full xl:w-auto">
                        {!isMasterApproved && (
                            <>
                                <Button variant="outline" onClick={() => setIsImportModalOpen(true)} className="px-5 rounded-xl border-slate-200 box-border flex items-center" style={{ height: '45px' }}>
                                    <Upload className="w-4 h-4 mr-2 shrink-0" /> Import
                                </Button>
                                <Button onClick={() => setIsSyncModalOpen(true)} className="px-5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold box-border flex items-center shadow-md shadow-orange-500/20 active:scale-95 transition-all" style={{ height: '45px' }}>
                                    {syncMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 shrink-0 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2 shrink-0" />}
                                    Sync Status
                                </Button>
                                <Button onClick={openAddModal} className="px-5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold box-border flex items-center shadow-md shadow-blue-500/20 active:scale-95 transition-all" style={{ height: '45px' }}>
                                    <Plus className="w-4 h-4 mr-2 shrink-0" /> Anggota
                                </Button>
                                <Button onClick={() => setIsApproveModalOpen(true)} className="px-5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-md shadow-emerald-500/20 box-border flex items-center active:scale-95 transition-all" style={{ height: '45px' }}>
                                    <CheckCircle2 className="w-4 h-4 mr-2 shrink-0" /> Approve & Generate
                                </Button>
                            </>
                        )}
                    </div>
                </div>

                {/* ✅ Filter Row 2 (Filter Dropdowns Tambahan) */}
                <div className="flex flex-wrap items-center gap-3 w-full border-t border-slate-100 dark:border-slate-800 pt-4">
                    <Select value={isValidFilter} onValueChange={(val) => { setIsValidFilter(val); setPage(1); }}>
                        <SelectTrigger className="w-[140px] rounded-xl border-slate-200 text-xs bg-white dark:bg-slate-900 box-border" style={{ height: '40px' }}>
                            <SelectValue placeholder="Validasi" />
                        </SelectTrigger>
                        <SelectContent className="font-bold">
                            <SelectItem value="all">Semua Validasi</SelectItem>
                            <SelectItem value="1">Siap Proses</SelectItem>
                            <SelectItem value="0">Bermasalah</SelectItem>
                        </SelectContent>
                    </Select>

                    <Select value={errorCodeFilter} onValueChange={(val) => { setErrorCodeFilter(val); setPage(1); }}>
                        <SelectTrigger className="w-[180px] rounded-xl border-slate-200 text-xs bg-white dark:bg-slate-900 box-border" style={{ height: '40px' }}>
                            <SelectValue placeholder="Kode Error" />
                        </SelectTrigger>
                        <SelectContent className="font-bold">
                            <SelectItem value="all">Semua Kode Error</SelectItem>
                            <SelectItem value="PACKAGE_NOT_FOUND">PACKAGE_NOT_FOUND</SelectItem>
                            <SelectItem value="OJS_NOT_FOUND">OJS_NOT_FOUND</SelectItem>
                            <SelectItem value="IN_OTHER_COLLECTIVE_DRAFT">IN_OTHER_COLLECTIVE_DRAFT</SelectItem>
                            <SelectItem value="OJS_NOT_READY_TO_PAY">OJS_NOT_READY_TO_PAY</SelectItem>
                            <SelectItem value="ALREADY_PAID_REGULAR">ALREADY_PAID_REGULAR</SelectItem>
                            <SelectItem value="HAS_UNPAID_REGULAR">HAS_UNPAID_REGULAR</SelectItem>
                        </SelectContent>
                    </Select>

                    <Select value={isPresenterFilter} onValueChange={(val) => { setIsPresenterFilter(val); setPage(1); }}>
                        <SelectTrigger className="w-[140px] rounded-xl border-slate-200 text-xs bg-white dark:bg-slate-900 box-border" style={{ height: '40px' }}>
                            <SelectValue placeholder="Peran" />
                        </SelectTrigger>
                        <SelectContent className="font-bold">
                            <SelectItem value="all">Semua Peran</SelectItem>
                            <SelectItem value="1">Presenter</SelectItem>
                            <SelectItem value="0">Non-Presenter</SelectItem>
                        </SelectContent>
                    </Select>

                    <Select value={participantTypeFilter} onValueChange={(val) => { setParticipantTypeFilter(val); setPage(1); }}>
                        <SelectTrigger className="w-[140px] rounded-xl border-slate-200 text-xs bg-white dark:bg-slate-900 box-border" style={{ height: '40px' }}>
                            <SelectValue placeholder="Tipe Peserta" />
                        </SelectTrigger>
                        <SelectContent className="font-bold">
                            <SelectItem value="all">Semua Tipe</SelectItem>
                            <SelectItem value="general">Umum</SelectItem>
                            <SelectItem value="student_s1_s2">Mhs S1/S2</SelectItem>
                            <SelectItem value="student_s3">Mhs S3</SelectItem>
                        </SelectContent>
                    </Select>

                    <Select value={verificationStatusFilter} onValueChange={(val) => { setVerificationStatusFilter(val); setPage(1); }}>
                        <SelectTrigger className="w-[160px] rounded-xl border-slate-200 text-xs bg-white dark:bg-slate-900 box-border" style={{ height: '40px' }}>
                            <SelectValue placeholder="Lifecycle" />
                        </SelectTrigger>
                        <SelectContent className="font-bold">
                            <SelectItem value="all">Semua Tahapan</SelectItem>
                            <SelectItem value="not_requested">Draft / Belum Aju</SelectItem>
                            <SelectItem value="pending">Menunggu Review</SelectItem>
                            <SelectItem value="approved">Disetujui</SelectItem>
                            <SelectItem value="rejected">Ditolak</SelectItem>
                        </SelectContent>
                    </Select>

                    {/* Limit dipindah ke row filter */}
                    <div className="ml-auto">
                        <Select value={perPage} onValueChange={(val) => { setPerPage(val); setPage(1); }}>
                            <SelectTrigger className="w-[110px] rounded-xl border-slate-200 text-xs bg-white dark:bg-slate-900 box-border flex items-center" style={{ height: '40px' }}>
                                <div className="flex items-center">
                                    <ListFilter size={14} className="mr-2 opacity-60 shrink-0" />
                                    <SelectValue placeholder="Limit" />
                                </div>
                            </SelectTrigger>
                            <SelectContent className="font-bold">
                                <SelectItem value="10">10 Baris</SelectItem>
                                <SelectItem value="50">50 Baris</SelectItem>
                                <SelectItem value="100">100 Baris</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

            </div>

            {/* --- TABLE MEMBERS --- */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden relative">
                {isFetching && !membersLoading && (
                    <div className="absolute inset-0 bg-white/50 dark:bg-slate-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    </div>
                )}

                <div className="overflow-x-auto pb-4">
                    <table className="w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider text-[10px]">
                            <tr>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Nama Partisipan</th>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Tipe & Format</th>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Naskah OJS</th>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Biaya</th>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Status Data</th>
                                {!isMasterApproved && <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 text-center sticky right-0 z-10 bg-slate-50 dark:bg-slate-800/90 shadow-[-10px_0_15px_-10px_rgba(0,0,0,0.1)]">Aksi</th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {membersLoading ? (
                                [...Array(5)].map((_, i) => <tr key={i}><td colSpan={6} className="h-20 animate-pulse bg-slate-50/30" /></tr>)
                            ) : !membersData || membersData.data.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="h-48 text-center text-slate-400">
                                        <Users className="w-12 h-12 mx-auto mb-3 opacity-20" />
                                        <p>Belum ada anggota di rombongan ini.</p>
                                    </td>
                                </tr>
                            ) : (
                                membersData.data.map((item: MemberData) => (
                                    <Fragment key={item.id}>
                                        <tr className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors bg-white dark:bg-slate-900 group">
                                            <td className="px-6 py-4">
                                                <p className="font-bold text-slate-900 dark:text-white text-sm">{item.participant_name}</p>
                                                <div className="flex flex-col mt-1 space-y-0.5">
                                                    <p className="text-[10px] text-slate-500 flex items-center gap-1.5"><Mail className="w-3 h-3 text-slate-400" /> {item.email}</p>
                                                    <p className="text-[10px] text-slate-500 flex items-center gap-1.5"><Phone className="w-3 h-3 text-slate-400" /> {item.whatsapp_number}</p>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <p className="font-semibold text-slate-700 dark:text-slate-300 text-xs uppercase">{item.participant_type.replace(/_/g, ' ')}</p>
                                                <p className="text-[10px] text-slate-500 uppercase font-bold mt-1">
                                                    {item.is_presenter ? `PRESENTER - ${item.presentation_format}` : 'NON-PRESENTER'}
                                                </p>
                                            </td>
                                            <td className="px-6 py-4 max-w-[250px] whitespace-normal">
                                                {item.is_presenter && item.ojs_submission_id ? (
                                                    <>
                                                        <p className="text-[10px] font-mono text-indigo-600 dark:text-indigo-400 font-bold mb-1">ID: #{item.ojs_submission_id}</p>
                                                        <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 line-clamp-2" title={item.submission_title || ""}>
                                                            {item.submission_title}
                                                        </p>
                                                    </>
                                                ) : (
                                                    <span className="text-xs italic text-slate-400">-</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 font-black text-slate-900 dark:text-white">
                                                {formatRupiah(item.individual_amount)}
                                            </td>
                                            <td className="px-6 py-4">
                                                {/* ✅ Render Status Verifikasi Lifecycle (Draft/Pending/Approved) */}
                                                <div className="mb-2">
                                                    {renderVerificationStatus(item.verification_status)}
                                                </div>
                                                {/* ✅ Render Validasi Badge beserta Error Code Custom Tooltip */}
                                                {renderValidationBadge(item.is_valid, item.error_code, item.validation_message)}
                                            </td>
                                            {!isMasterApproved && (
                                                <td className="px-6 py-4 text-center sticky right-0 z-10 bg-white dark:bg-slate-900 group-hover:bg-slate-50/50 dark:group-hover:bg-slate-800/80 shadow-[-10px_0_15px_-10px_rgba(0,0,0,0.1)] transition-colors">
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger
                                                            className="h-8 px-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-semibold shadow-sm flex items-center gap-1.5 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700 mx-auto outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            Aksi <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end" className="w-40 rounded-xl z-[99] bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-xl">
                                                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditModal(item); }} className="cursor-pointer py-2.5 hover:bg-slate-50 dark:hover:bg-slate-800">
                                                                <Pencil className="w-4 h-4 mr-3 text-amber-500" />
                                                                <span className="font-semibold text-slate-700 dark:text-slate-300">Edit Anggota</span>
                                                            </DropdownMenuItem>
                                                            {item.error_code === 'HAS_UNPAID_REGULAR' && (
                                                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setSelectedMember(item); setIsCancelInvoiceModalOpen(true); }} className="cursor-pointer py-2.5 hover:bg-slate-50 dark:hover:bg-slate-800">
                                                                    <XCircle className="w-4 h-4 mr-3 text-orange-500" />
                                                                    <span className="font-semibold text-slate-700 dark:text-slate-300">Hapus Tagihan</span>
                                                                </DropdownMenuItem>
                                                            )}
                                                            <DropdownMenuSeparator className="bg-slate-100 dark:bg-slate-800" />
                                                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setSelectedMember(item); setIsDeleteModalOpen(true); }} className="cursor-pointer py-2.5 text-rose-600 focus:text-rose-700 focus:bg-rose-50 dark:focus:bg-rose-950/30">
                                                                <Trash2 className="w-4 h-4 mr-3" />
                                                                <span className="font-semibold">Hapus Anggota</span>
                                                            </DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </td>
                                            )}
                                        </tr>
                                    </Fragment>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* PAGINATION */}
                {membersData && membersData.total > 0 && (
                    <div className="p-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 flex justify-between items-center text-sm text-slate-500">
                        <span>Menampilkan {membersData.from} - {membersData.to} dari {membersData.total}</span>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1} className="h-9 px-3 rounded-lg"><ChevronLeft className="w-4 h-4 mr-1" /> Prev</Button>
                            <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page === membersData.last_page} className="h-9 px-3 rounded-lg">Next <ChevronRight className="w-4 h-4 ml-1" /></Button>
                        </div>
                    </div>
                )}
            </div>

            {/* ==================================================================== */}
            {/* MODAL FORM TAMBAH / EDIT ANGGOTA (Z-INDEX 999 AGAR TOAST MUNCUL) */}
            {/* ==================================================================== */}
            {isFormModalOpen && createPortal(
                <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
                    <div className="bg-white dark:bg-slate-900 rounded-[2rem] shadow-2xl max-w-2xl w-full flex flex-col max-h-[90vh] overflow-hidden animate-in zoom-in-95">
                        <div className="p-6 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 flex justify-between items-center">
                            <h3 className="font-black text-xl flex items-center gap-2 text-slate-900 dark:text-white">
                                <Users className="w-5 h-5 text-blue-600" /> {selectedMember ? "Edit Anggota" : "Tambah Anggota"}
                            </h3>
                            <button onClick={closeFormModal} className="p-2 bg-slate-200/50 hover:bg-slate-200 rounded-full transition-colors"><XCircle className="w-5 h-5 text-slate-500" /></button>
                        </div>

                        <form onSubmit={(e) => { e.preventDefault(); saveMutation.mutate(formData); }} className="p-6 overflow-y-auto space-y-5">

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-500 uppercase">1. Pilih Konferensi Tujuan</label>
                                <Select required value={formData.conference_id} onValueChange={(val) => setFormData({ ...formData, conference_id: val, ojs_submission_id: "" })}>
                                    <SelectTrigger className="w-full rounded-xl bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700" style={{ height: '45px' }}>
                                        <SelectValue placeholder="Pilih Konferensi Tujuan" />
                                    </SelectTrigger>
                                    <SelectContent className="z-[9999] font-bold">
                                        {conferencesData?.map(c => (
                                            <SelectItem key={c.conference_id} value={String(c.conference_id)}>
                                                {c.slug.toUpperCase()} - {c.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500 uppercase">Tipe Peserta</label>
                                    <Select value={formData.participant_type} onValueChange={(val) => setFormData({ ...formData, participant_type: val })}>
                                        <SelectTrigger className="w-full rounded-xl" style={{ height: '45px' }}>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="z-[9999] font-bold">
                                            <SelectItem value="general">Umum (General)</SelectItem>
                                            <SelectItem value="student_s1_s2">Mahasiswa S1/S2</SelectItem>
                                            <SelectItem value="student_s3">Mahasiswa S3</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2 pt-8">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" checked={formData.is_internal_student} onChange={(e) => setFormData({ ...formData, is_internal_student: e.target.checked })} className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                                        <span className="text-sm font-bold text-slate-700 dark:text-slate-300">Merupakan Mahasiswa Internal (Undiksha)</span>
                                    </label>
                                </div>
                            </div>

                            {/* --- SEKSI OJS & PRESENTER --- */}
                            <div className="space-y-4 p-5 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
                                <label className="flex items-center gap-2 cursor-pointer border-b border-slate-200 dark:border-slate-700 pb-3">
                                    <input type="checkbox" checked={formData.is_presenter} onChange={(e) => setFormData({ ...formData, is_presenter: e.target.checked })} className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                                    <span className="text-sm font-black text-slate-800 dark:text-slate-200 uppercase tracking-widest">Daftar Sebagai Presenter (Pemakalah)</span>
                                </label>

                                {formData.is_presenter && (
                                    <div className="space-y-4 pt-2 animate-in fade-in slide-in-from-top-2">
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-slate-500 uppercase flex justify-between">
                                                <span>Pilih Naskah OJS (Telah di-Review)</span>
                                                {ojsLoading && <span className="text-blue-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading...</span>}
                                            </label>

                                            <Select required={formData.is_presenter} value={formData.ojs_submission_id} onValueChange={handleOjsSelect}>
                                                <SelectTrigger className="w-full rounded-xl bg-white dark:bg-slate-900 border-blue-200 focus:ring-blue-500 overflow-hidden" style={{ height: '45px' }}>
                                                    <SelectValue placeholder="-- Silakan Pilih Naskah --" className="truncate" />
                                                </SelectTrigger>
                                                <SelectContent className="z-[9999] max-h-[300px] w-[var(--radix-select-trigger-width)]">
                                                    {formData.ojs_submission_id && selectedOjsLabel && !selectedOjsOption && (
                                                        <SelectItem value={formData.ojs_submission_id} disabled className="hidden">
                                                            {selectedOjsLabel}
                                                        </SelectItem>
                                                    )}
                                                    <div className="p-2 sticky top-0 bg-white dark:bg-slate-900 z-10 border-b border-slate-100 dark:border-slate-800">
                                                        <div className="relative">
                                                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                                            <Input
                                                                placeholder="Cari ID, Judul, atau Author..."
                                                                value={ojsSearch}
                                                                onChange={(e) => setOjsSearch(e.target.value)}
                                                                onKeyDown={(e) => e.stopPropagation()}
                                                                onClick={(e) => e.preventDefault()}
                                                                className="h-9 pl-9 text-xs rounded-md w-full"
                                                            />
                                                        </div>
                                                    </div>

                                                    {filteredOjsList?.length === 0 ? (
                                                        <div className="p-4 text-xs text-slate-500 text-center font-bold">Naskah tidak ditemukan.</div>
                                                    ) : (
                                                        filteredOjsList?.map(ojs => (
                                                            <SelectItem key={ojs.ojs_submission_id} value={String(ojs.ojs_submission_id)}>
                                                                <div className="flex flex-col py-1 text-left max-w-full">
                                                                    <span className="font-bold text-blue-600 text-[10px]">ID: #{ojs.ojs_submission_id}</span>
                                                                    <span className="font-bold text-sm truncate w-full whitespace-normal leading-snug break-words">{ojs.title}</span>
                                                                </div>
                                                            </SelectItem>
                                                        ))
                                                    )}
                                                </SelectContent>
                                            </Select>
                                            <p className="text-[10px] text-emerald-600 font-bold">Pilih naskah untuk mengisi data partisipan secara otomatis.</p>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-slate-500 uppercase">Format Presentasi</label>
                                            <Select value={formData.presentation_format} onValueChange={(val) => setFormData({ ...formData, presentation_format: val })}>
                                                <SelectTrigger className="w-full rounded-xl" style={{ height: '45px' }}>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent className="z-[9999] font-bold">
                                                    <SelectItem value="oral">Oral (Luring)</SelectItem>
                                                    <SelectItem value="online">Online (Daring)</SelectItem>
                                                    <SelectItem value="poster">Poster</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Data Diri */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2 md:col-span-2">
                                    <label className="text-xs font-bold text-slate-500 uppercase">Nama Lengkap Partisipan</label>
                                    <Input required value={formData.participant_name} onChange={e => setFormData({ ...formData, participant_name: e.target.value })} className="h-12 rounded-xl font-bold" />
                                </div>
                                <div className="space-y-2 md:col-span-2">
                                    <label className="text-xs font-bold text-slate-500 uppercase">Instansi / Afiliasi</label>
                                    <Input required value={formData.institution_name} onChange={e => setFormData({ ...formData, institution_name: e.target.value })} className="h-12 rounded-xl font-bold" />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500 uppercase">Email Partisipan</label>
                                    <Input required type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} className="h-12 rounded-xl font-bold" />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500 uppercase">No WhatsApp</label>
                                    <Input required value={formData.whatsapp_number} onChange={e => setFormData({ ...formData, whatsapp_number: e.target.value })} className="h-12 rounded-xl font-bold" />
                                </div>
                            </div>

                            <div className="pt-4 flex justify-end gap-3 border-t border-slate-100 dark:border-slate-800 mt-6">
                                <Button type="button" variant="ghost" onClick={closeFormModal} className="h-12 px-6 rounded-xl font-bold">Batal</Button>
                                <Button type="submit" disabled={saveMutation.isPending} className="h-12 px-8 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold transition-all active:scale-95">
                                    {saveMutation.isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : "Simpan Anggota"}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body
            )}

            {/* --- MODAL IMPORT EXCEL --- */}
            {isImportModalOpen && createPortal(
                <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
                    <div className="bg-white dark:bg-slate-900 rounded-[2rem] shadow-2xl max-w-lg w-full p-8 text-center animate-in zoom-in-95 max-h-[90vh] flex flex-col">
                        <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 shrink-0"><Upload className="w-8 h-8" /></div>
                        <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-2 shrink-0">Import via Excel</h3>
                        <p className="text-slate-500 text-sm mb-6 leading-relaxed shrink-0">Unggah file template Excel yang telah diisi untuk memasukkan banyak anggota sekaligus.</p>

                        <div className="overflow-y-auto mb-6 text-left space-y-4 px-1">
                            {/* Template Download */}
                            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 p-4 rounded-xl flex items-start justify-between gap-4">
                                <div>
                                    <p className="text-sm font-bold text-blue-700 dark:text-blue-400">Unduh Format Template</p>
                                    <p className="text-xs text-blue-600 dark:text-blue-500 mt-1">Gunakan template ini agar format data sesuai dengan sistem.</p>
                                </div>
                                <Button variant="outline" size="sm" onClick={() => window.open("/templates/template_import_kolektif_conference.xlsx", "_blank")} className="shrink-0 bg-white dark:bg-slate-900 border-blue-200 text-blue-600 hover:bg-blue-100">
                                    <Download className="w-4 h-4 mr-2" /> Template
                                </Button>
                            </div>

                            {/* Conference Reference Table */}
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-500 uppercase">Referensi ID Konferensi</label>
                                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden max-h-[150px] overflow-y-auto">
                                    <table className="w-full text-left text-xs">
                                        <thead className="bg-slate-100 dark:bg-slate-800 sticky top-0">
                                            <tr>
                                                <th className="p-2 font-bold text-slate-600 dark:text-slate-300 border-b dark:border-slate-700">ID</th>
                                                <th className="p-2 font-bold text-slate-600 dark:text-slate-300 border-b dark:border-slate-700">Slug</th>
                                                <th className="p-2 font-bold text-slate-600 dark:text-slate-300 border-b dark:border-slate-700">Nama Konferensi</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {conferencesData?.map((conf) => (
                                                <tr key={conf.conference_id} className="border-b border-slate-100 dark:border-slate-800/50 last:border-0 hover:bg-slate-100 dark:hover:bg-slate-800/80">
                                                    <td className="p-2 font-mono font-bold text-blue-600 dark:text-blue-400">{conf.conference_id}</td>
                                                    <td className="p-2 font-semibold text-slate-700 dark:text-slate-300">{conf.slug.toUpperCase()}</td>
                                                    <td className="p-2 text-slate-600 dark:text-slate-400 truncate max-w-[150px]" title={conf.name}>{conf.name}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* File Input */}
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-500 uppercase">Upload File Excel</label>
                                <Input type="file" accept=".xlsx, .xls" onChange={(e) => setImportFile(e.target.files?.[0] || null)} className="h-12 rounded-xl pt-2.5 cursor-pointer font-bold" />
                            </div>
                        </div>

                        <div className="flex gap-3 shrink-0">
                            <Button variant="outline" onClick={() => { setIsImportModalOpen(false); setImportFile(null); }} className="flex-1 h-12 rounded-xl font-bold">Batal</Button>
                            <Button onClick={() => importFile && importMutation.mutate(importFile)} disabled={!importFile || importMutation.isPending} className="flex-1 h-12 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold">
                                {importMutation.isPending ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Proses Import"}
                            </Button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* --- MODAL APPROVE / GENERATE VA --- */}
            {isApproveModalOpen && createPortal(
                <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
                    <div className="bg-white dark:bg-slate-900 rounded-[2rem] shadow-2xl max-w-md w-full p-8 text-center animate-in zoom-in-95">
                        <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="w-8 h-8" /></div>
                        <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-2">Generate Tagihan VA</h3>
                        <p className="text-slate-500 text-sm mb-6 leading-relaxed">Pastikan semua data anggota berstatus <span className="font-bold text-emerald-500">VALID</span>. Tindakan ini akan mengunci data anggota dan menerbitkan Virtual Account secara permanen.</p>

                        <div className="mb-8 text-left space-y-2">
                            <label className="text-xs font-bold text-slate-500 uppercase">Pilih Payment Gateway</label>
                            <Select value={gatewayId} onValueChange={setGatewayId}>
                                <SelectTrigger className="w-full rounded-xl border-slate-200 bg-slate-50 dark:bg-slate-800" style={{ height: '45px' }}>
                                    <SelectValue placeholder="Pilih Bank / Metode" />
                                </SelectTrigger>
                                <SelectContent className="z-[9999] font-bold">
                                    <SelectItem value="8">BNI Virtual Account (IDR)</SelectItem>
                                    <SelectItem value="9">BNI Virtual Account (USD)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="flex gap-3">
                            <Button variant="outline" onClick={() => setIsApproveModalOpen(false)} className="flex-1 h-12 rounded-xl font-bold">Cek Kembali</Button>
                            <Button onClick={() => approveMutation.mutate()} disabled={approveMutation.isPending} className="flex-1 h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
                                {approveMutation.isPending ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Ya, Generate VA"}
                            </Button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* --- MODAL DELETE MEMBER --- */}
            {isDeleteModalOpen && createPortal(
                <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
                    <div className="bg-white dark:bg-slate-900 rounded-[2rem] shadow-2xl max-w-sm w-full p-8 text-center animate-in zoom-in-95">
                        <div className="w-16 h-16 bg-rose-100 dark:bg-rose-900/30 text-rose-600 rounded-full flex items-center justify-center mx-auto mb-4"><Trash2 className="w-8 h-8" /></div>
                        <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-2">Hapus Anggota?</h3>
                        <p className="text-slate-500 text-sm mb-8 leading-relaxed">Anda yakin ingin mengeluarkan <strong>{selectedMember?.participant_name}</strong> dari tagihan kolektif ini?</p>
                        <div className="flex gap-3">
                            <Button variant="outline" onClick={() => setIsDeleteModalOpen(false)} className="flex-1 h-12 rounded-xl font-bold">Batal</Button>
                            <Button onClick={() => selectedMember && deleteMutation.mutate(selectedMember.id)} disabled={deleteMutation.isPending} className="flex-1 h-12 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-bold">
                                {deleteMutation.isPending ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Ya, Hapus"}
                            </Button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* --- MODAL CANCEL INVOICE --- */}
            {isCancelInvoiceModalOpen && createPortal(
                <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
                    <div className="bg-white dark:bg-slate-900 rounded-[2rem] shadow-2xl max-w-sm w-full p-8 text-center animate-in zoom-in-95">
                        <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-4"><XCircle className="w-8 h-8" /></div>
                        <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-2">Hapus Tagihan?</h3>
                        <p className="text-slate-500 text-sm mb-8 leading-relaxed">Anda yakin ingin membatalkan tagihan reguler untuk <strong>{selectedMember?.participant_name}</strong>?</p>
                        <div className="flex gap-3">
                            <Button variant="outline" onClick={() => setIsCancelInvoiceModalOpen(false)} className="flex-1 h-12 rounded-xl font-bold">Batal</Button>
                            <Button onClick={() => selectedMember && cancelInvoiceMutation.mutate(selectedMember.id)} disabled={cancelInvoiceMutation.isPending} className="flex-1 h-12 rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-bold">
                                {cancelInvoiceMutation.isPending ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Ya, Hapus"}
                            </Button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* --- MODAL SYNC STATUS --- */}
            {isSyncModalOpen && createPortal(
                <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
                    <div className="bg-white dark:bg-slate-900 rounded-[2rem] shadow-2xl max-w-sm w-full p-8 text-center animate-in zoom-in-95">
                        <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-4"><RefreshCw className="w-8 h-8" /></div>
                        <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-2">Sync Status?</h3>
                        <p className="text-slate-500 text-sm mb-8 leading-relaxed">Anda yakin ingin melakukan sinkronisasi status semua anggota di tagihan kolektif ini?</p>
                        <div className="flex gap-3">
                            <Button variant="outline" onClick={() => setIsSyncModalOpen(false)} className="flex-1 h-12 rounded-xl font-bold">Batal</Button>
                            <Button onClick={() => syncMutation.mutate()} disabled={syncMutation.isPending} className="flex-1 h-12 rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-bold">
                                {syncMutation.isPending ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Ya, Sync"}
                            </Button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}